/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Table from 'components/generics/Table/ReactTable';
import Button from 'components/generics/Button/Button';
import Modal from 'components/generics/Modal/Modal';
import ConfirmDelete from 'components/generics/ConfirmDelete/ConfirmDelete';
import CourseTitle from 'components/generics/CourseTitle/CourseTitle';
import ChecklistFillModal from 'components/generics/Checklist/ChecklistFillModal';
import useAuth from 'providers/Auth/useAuth';

import { deleteInstance, getAllInstances } from 'services/API/Checklist';
import { subscribeChecklistEvents } from 'services/API/ChecklistRealtime';

import {
  formatDateAndHour,
  formatMinutesToReadableHourOfDay,
  isAuthorised,
  prepareTable,
} from 'utils/func';
import { CHECKLIST_INSTANCE_HEADERS } from 'utils/const';

import 'components/generics/Checklist/Checklist.style.scss';

const SECTIONS = [
  { type: 'stage', title: 'Stages' },
  { type: 'airport', title: 'Airports' },
  { type: 'flight', title: 'Flights' },
];

const errorMessage = err => err?.response?.data?.message || err?.message || 'Unexpected error';

/**
 * Enrichit une instance pour l'affichage :
 * - `entity` identifie l'entité (vol : id + route + heure de décollage) ;
 * - `lastChange` = dernier point modifié (email + date/heure).
 */
const enrich = instance => {
  let entity = instance.entityLabel || '';
  if (instance.entityType === 'flight') {
    const takeoff = instance.flight?.takeoffTimeUtc;
    const takeoffLabel =
      typeof takeoff === 'number' ? ` · ${formatMinutesToReadableHourOfDay(takeoff)} UTC` : '';
    entity = `#${instance.flightId} · ${instance.entityLabel}${takeoffLabel}`;
  }

  const modified = (instance.items || []).filter(item => item.lastModifiedAt);
  let lastChange = '';
  if (modified.length > 0) {
    const last = modified.reduce((a, b) =>
      new Date(a.lastModifiedAt) > new Date(b.lastModifiedAt) ? a : b,
    );
    lastChange = `${last.lastModifiedByEmail || ''} · ${formatDateAndHour(
      new Date(last.lastModifiedAt),
    )}`;
  }

  return { ...instance, entity, lastChange };
};

/**
 * Gestion de toutes les instances de checklist de la course, par type
 * (stages, aéroports, vols), triées par jour puis alphabétiquement.
 */
const ChecklistInstances = () => {
  const { user } = useAuth();
  const canDelete = isAuthorised(['Admin', 'CPSAdmin'], user?.roleName);
  const canEdit = isAuthorised(
    ['Admin', 'CPSAdmin', 'CPSUser', 'DISPATCH', 'CREW'],
    user?.roleName,
  );

  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState({ stage: [], airport: [], flight: [] });
  const [deleteType, setDeleteType] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [editInstanceId, setEditInstanceId] = useState(null);
  const mounted = useRef(true);

  const load = () => {
    getAllInstances()
      .then(res => {
        if (mounted.current) {
          setInstances(res.data || []);
        }
      })
      .catch(err => {
        if (mounted.current) toast.error(errorMessage(err));
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
  };

  useEffect(() => {
    mounted.current = true;
    load();
    const unsubscribe = subscribeChecklistEvents(() => load());
    return () => {
      mounted.current = false;
      unsubscribe();
    };
  }, []);

  const onSelectionChange = useMemo(
    () => ({
      stage: selection =>
        setSelections(prev => (prev.stage === selection ? prev : { ...prev, stage: selection })),
      airport: selection =>
        setSelections(prev => (prev.airport === selection ? prev : { ...prev, airport: selection })),
      flight: selection =>
        setSelections(prev => (prev.flight === selection ? prev : { ...prev, flight: selection })),
    }),
    [],
  );

  const onDelete = () => {
    const selection = selections[deleteType] || [];
    if (selection.length === 0) return;
    Promise.all(selection.map(row => deleteInstance(row.original.id, true)))
      .then(() => {
        toast.success(`${selection.length} checklist(s) deleted`);
        setSelections(prev => ({ ...prev, [deleteType]: [] }));
        setDeleteType(null);
        load();
      })
      .catch(err => toast.error(errorMessage(err)));
  };

  const closeEditModal = () => {
    setEditInstanceId(null);
    load();
  };

  const onDeleteRow = () => {
    if (!deleteRow) return;
    deleteInstance(deleteRow.id, true)
      .then(() => {
        toast.success('Checklist deleted');
        setDeleteRow(null);
        load();
      })
      .catch(err => toast.error(errorMessage(err)));
  };

  const rowsByType = useMemo(() => {
    const enrichedByType = { stage: [], airport: [], flight: [] };
    instances.forEach(instance => {
      if (enrichedByType[instance.entityType]) {
        enrichedByType[instance.entityType].push(enrich(instance));
      }
    });
    return {
      stage: prepareTable(CHECKLIST_INSTANCE_HEADERS, enrichedByType.stage),
      airport: prepareTable(CHECKLIST_INSTANCE_HEADERS, enrichedByType.airport),
      flight: prepareTable(CHECKLIST_INSTANCE_HEADERS, enrichedByType.flight),
    };
  }, [instances]);

  const completeCount = instances.filter(instance => instance.state?.isComplete).length;
  const allValidCount = instances.filter(instance => instance.state?.isAllValid).length;

  return (
    <div className="params crud checklist-instances" id="checklists">
      <div className="crud__header">
        <div>
          <h1>Checklists</h1>
          <h2>
            <CourseTitle />
          </h2>
        </div>
      </div>

      <p className="checklist-instances__summary">
        {instances.length} instance(s) · {completeCount} completed · {allValidCount} 100% valid
      </p>

      {loading ? (
        <p className="checklist-instances__empty">Loading…</p>
      ) : (
        SECTIONS.map(section => {
          const rows = rowsByType[section.type];
          const selected = selections[section.type] || [];
          return (
            <div className="checklist-instances__section" key={section.type}>
              <div className="checklist-instances__section-header">
                <h2>
                  {section.title} <span className="count">({rows.length})</span>
                </h2>
                {canDelete && (
                  <Button
                    text="Delete"
                    disabled={selected.length === 0}
                    defaultStyle={`primary${selected.length === 0 ? ' disabled' : ''}`}
                    onClick={() => setDeleteType(section.type)}
                  />
                )}
              </div>
              {rows.length === 0 ? (
                <p className="checklist-instances__empty">No checklist instance.</p>
              ) : (
                <Table
                  selectedRows={onSelectionChange[section.type]}
                  datas={rows}
                  headers={CHECKLIST_INSTANCE_HEADERS}
                  editable
                  onEdit={row => setEditInstanceId(row.id)}
                  onDelete={canDelete ? row => setDeleteRow(row) : undefined}
                  name={`checklistInstances-${section.type}`}
                />
              )}
            </div>
          );
        })
      )}

      {editInstanceId && (
        <Modal onClose={closeEditModal}>
          <ChecklistFillModal instanceId={editInstanceId} onClose={closeEditModal} canEdit={canEdit} />
        </Modal>
      )}

      {deleteType && (
        <ConfirmDelete
          onClose={() => setDeleteType(null)}
          list={selections[deleteType]}
          onConfirm={onDelete}
          formatList={row => `${row.date} — ${row.entity}`}
        />
      )}

      {deleteRow && (
        <ConfirmDelete
          onClose={() => setDeleteRow(null)}
          list={[deleteRow]}
          onConfirm={onDeleteRow}
          formatList={row => `${row.date} — ${row.entity}`}
        />
      )}
    </div>
  );
};

export default ChecklistInstances;
